/**
 * Public Booking Grid — /contact
 * Lit getPublicStaffAvailability (Cloud Function publique, filtrée côté
 * serveur) au lieu de la collection agentSettings (morte — jamais écrite).
 * Retient les staffId réels par créneau, pas seulement les noms, pour
 * permettre une réservation fonctionnelle via bookStaffCall.
 */

import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { invokeFunction } from "@/lib/cloudrun";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Users, Clock, CalendarDays, Loader2, CheckCircle } from "lucide-react";

const translations = {
  en: {
    title: "Team Availability",
    subtitle: "See when our experts are available — updated regularly",
    selectDate: "Select a Date",
    availableSlots: "Available Time Slots",
    slotsDescription: "Times when team members are available for calls",
    noAvailability: "No availability on this date",
    tryAnotherDate: "Please select another date",
    teamMembers: "Team Members",
    available: "Available",
    offline: "Offline",
    busy: "Busy",
    away: "Away",
    loading: "Loading availability...",
    today: "Today",
    tomorrow: "Tomorrow",
    minutes: "min",
    staffAvailable: "staff available",
  },
  fr: {
    title: "Disponibilités de l'Équipe",
    subtitle: "Consultez les disponibilités de nos experts — mise à jour régulière",
    selectDate: "Sélectionnez une Date",
    availableSlots: "Créneaux Disponibles",
    slotsDescription: "Horaires où les membres de l'équipe sont disponibles",
    noAvailability: "Aucune disponibilité à cette date",
    tryAnotherDate: "Veuillez sélectionner une autre date",
    teamMembers: "Membres de l'Équipe",
    available: "Disponible",
    offline: "Hors ligne",
    busy: "Occupé",
    away: "Absent",
    loading: "Chargement des disponibilités...",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    minutes: "min",
    staffAvailable: "membres disponibles",
  }
};

interface StaffSlot { dayOfWeek: number; startTime: string; endTime: string }
interface StaffMember {
  staffId: string;
  displayName: string;
  currentStatus: 'available' | 'busy' | 'away' | 'offline';
  slots: StaffSlot[];
  timezone: string;
  slotDuration: number;
  bufferBetweenMeetings: number;
}
interface TimeSlot {
  start: Date;
  end: Date;
  staffIds: string[];
  staffNames: string[];
}

interface StaffAvailabilityPublicProps {
  onSlotSelect?: (slot: TimeSlot) => void;
  selectedSlotTime?: string | null;
  filterLanguage?: string | null; // conservé pour compat, non filtré ici faute de données de langue côté userAvailability
}

export function StaffAvailabilityPublic({ onSlotSelect, selectedSlotTime }: StaffAvailabilityPublicProps) {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadAvailability = useCallback(async () => {
    try {
      const res = await invokeFunction<{ staff: StaffMember[] }>(
        'get-public-staff-availability',
        undefined,
        { requireAuth: false, method: 'GET' }
      );
      setStaffMembers(res?.staff || []);
    } catch (err) {
      console.error('[StaffAvailabilityPublic] load failed:', err);
      setStaffMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAvailability();
    // Rafraîchit toutes les 60s — pas de temps réel via Cloud Function HTTP simple,
    // mais suffisant pour une grille de réservation publique.
    const interval = setInterval(loadAvailability, 60_000);
    return () => clearInterval(interval);
  }, [loadAvailability]);

  const calculateAvailableSlots = useCallback(() => {
    const slotDuration = staffMembers[0]?.slotDuration || 30;
    const buffer = staffMembers[0]?.bufferBetweenMeetings ?? 15;
    const dayOfWeek = selectedDate.getDay();
    const now = new Date();
    const slots: TimeSlot[] = [];

    const eligibleStaff = staffMembers.filter((s) => s.currentStatus !== 'offline');
    const allRanges: { start: number; end: number; staffId: string; name: string }[] = [];
    eligibleStaff.forEach((staff) => {
      staff.slots
        .filter((s) => s.dayOfWeek === dayOfWeek)
        .forEach((s) => {
          const [sh, sm] = s.startTime.split(':').map(Number);
          const [eh, em] = s.endTime.split(':').map(Number);
          allRanges.push({ start: sh * 60 + sm, end: eh * 60 + em, staffId: staff.staffId, name: staff.displayName });
        });
    });

    if (allRanges.length === 0) {
      setAvailableSlots([]);
      return;
    }

    const earliest = Math.min(...allRanges.map((r) => r.start));
    const latest = Math.max(...allRanges.map((r) => r.end));

    for (let minutes = earliest; minutes + slotDuration <= latest; minutes += slotDuration + buffer) {
      const slotStart = new Date(selectedDate);
      slotStart.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + slotDuration);

      if (slotStart <= now) continue;

      const staffInSlot = allRanges.filter((r) => r.start <= minutes && r.end >= minutes + slotDuration);
      if (staffInSlot.length > 0) {
        slots.push({
          start: slotStart,
          end: slotEnd,
          staffIds: [...new Set(staffInSlot.map((s) => s.staffId))],
          staffNames: [...new Set(staffInSlot.map((s) => s.name))],
        });
      }
    }
    setAvailableSlots(slots);
  }, [selectedDate, staffMembers]);

  useEffect(() => {
    if (!isLoading) calculateAvailableSlots();
  }, [selectedDate, staffMembers, isLoading, calculateAvailableSlots]);

  const formatTime = (date: Date) =>
    date.toLocaleTimeString(language === 'fr' ? 'fr-CA' : 'en-US', {
      hour: '2-digit', minute: '2-digit', hour12: language !== 'fr',
    });

  const formatDateLabel = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === today.toDateString()) return t.today;
    if (date.toDateString() === tomorrow.toDateString()) return t.tomorrow;
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  };

  const statusEmoji: Record<string, string> = { available: '🟢', busy: '🔴', away: '🟡', offline: '⚫' };
  const statusLabel = (status: string) =>
    ({ available: t.available, busy: t.busy, away: t.away, offline: t.offline }[status] || status);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#CEAB65]" />
            <span className="ml-2 text-muted-foreground">{t.loading}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const onlineStaff = staffMembers.filter((s) => s.currentStatus !== 'offline');

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] text-white">
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#CEAB65]" />
          {t.title}
        </CardTitle>
        <CardDescription className="text-white/80">{t.subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#CEAB65]" />
              {t.selectDate}
            </h4>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border"
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            />

            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t.teamMembers}
              </h5>
              <div className="space-y-1.5">
                {staffMembers.map((staff) => (
                  <div key={staff.staffId} className="flex items-center gap-2 text-sm">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-[#1D314D] text-white">
                        {staff.displayName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate">{staff.displayName}</span>
                    <span className="text-xs">
                      {statusEmoji[staff.currentStatus] || '⚫'} {statusLabel(staff.currentStatus)}
                    </span>
                  </div>
                ))}
                {staffMembers.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t.noAvailability}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3">
              {t.availableSlots}
              <Badge variant="secondary" className="ml-2">{formatDateLabel(selectedDate)}</Badge>
              {onlineStaff.length > 0 && (
                <Badge className="ml-2 bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                  {onlineStaff.length} {t.available}
                </Badge>
              )}
            </h4>
            <p className="text-sm text-muted-foreground mb-4">{t.slotsDescription}</p>

            {availableSlots.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-2">
                {availableSlots.map((slot, index) => {
                  const slotTimeKey = slot.start.toISOString();
                  const isSelected = selectedSlotTime === slotTimeKey;
                  return (
                    <Button
                      key={index}
                      variant={isSelected ? "default" : "outline"}
                      className={`flex flex-col items-start h-auto py-3 px-3 ${
                        isSelected ? "bg-[#1D314D] text-white border-[#CEAB65]" : "hover:bg-[#1D314D]/10 hover:border-[#CEAB65]"
                      }`}
                      onClick={() => onSlotSelect?.(slot)}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="font-semibold text-base">{formatTime(slot.start)}</span>
                        {isSelected && <CheckCircle className="h-4 w-4 text-[#CEAB65] ml-auto" />}
                      </div>
                      <div className="flex items-center gap-1 text-xs opacity-70 mt-1">
                        <Users className="h-3 w-3" />
                        <span>{slot.staffIds.length} {t.staffAvailable}</span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-muted/30 rounded-lg">
                <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">{t.noAvailability}</p>
                <p className="text-sm text-muted-foreground mt-1">{t.tryAnotherDate}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}